/**
 * Pure calculation functions for savings goals
 * These functions take goal data and transactions as inputs and return calculated values
 * 
 * IMPORTANT: These functions now receive ALL transactions (including historical)
 * to properly calculate progress for goals that span multiple months.
 * The main budget calculations still use current month transactions only.
 */

// Utility functions for date calculations
export const daysBetween = (startDate, endDate) => {
    try {
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Validate dates
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            console.warn('Invalid date provided to daysBetween:', { startDate, endDate });
            return 0;
        }

        const diffTime = Math.abs(end - start);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch (error) {
        console.error('Error calculating days between dates:', error, { startDate, endDate });
        return 0;
    }
};

export const addDays = (date, days) => {
    try {
        const result = new Date(date);

        // Validate input date
        if (isNaN(result.getTime())) {
            console.warn('Invalid date provided to addDays:', { date, days });
            return new Date().toISOString(); // Return current date as fallback
        }

        // Validate days parameter
        if (typeof days !== 'number' || isNaN(days)) {
            console.warn('Invalid days parameter provided to addDays:', { date, days });
            return result.toISOString(); // Return original date
        }

        result.setDate(result.getDate() + days);
        return result.toISOString();
    } catch (error) {
        console.error('Error adding days to date:', error, { date, days });
        return new Date().toISOString(); // Return current date as fallback
    }
};

// Calculate progress from actual transactions (includes all historical transactions for multi-month goals)
export const getGoalProgress = (goalId, transactions) => {
    if (!Array.isArray(transactions)) {
        console.log('return NONE');
        return 0;
    }
    const val = transactions
        .filter(t => t.type === 'savings' && t.goal === goalId)
        .reduce((sum, t) => sum + (t.amount || 0), 0);

    return val;
};

// Calculate dynamic status based on transaction progress
export const getGoalStatus = (goal, transactions) => {
    // Only 'paused' is stored as a manual state - respect it
    if (goal.status === 'paused') {
        return 'paused';
    }

    // Calculate actual progress from transactions
    const actualProgress = getGoalProgress(goal.id, transactions);

    // If progress meets or exceeds target, show as completed (but don't store it)
    if (actualProgress >= goal.targetAmount) {
        return 'completed';
    }

    // Otherwise, it's active (default state)
    return 'active';
};

// Calculate active days elapsed (excluding paused time)
export const getGoalActiveDaysElapsed = (goal) => {
    const totalDaysElapsed = daysBetween(goal.createdDate, new Date());
    return totalDaysElapsed - (goal.totalPausedDays || 0);
};

// Calculate remaining days for the goal
export const getGoalRemainingDays = (goal) => {
    const activeDaysElapsed = getGoalActiveDaysElapsed(goal);
    return Math.max(0, goal.duration - activeDaysElapsed);
};

// Calculate progress percentage
export const getGoalProgressPercentage = (goal, transactions) => {
    const actualProgress = getGoalProgress(goal.id, transactions);
    return Math.min(100, (actualProgress / goal.targetAmount) * 100);
};

// Calculate daily allocation based on remaining amount and days
export const getGoalDailyAllocation = (goal, transactions) => {
    const status = getGoalStatus(goal, transactions);
    if (status !== 'active') return 0;

    const actualProgress = getGoalProgress(goal.id, transactions);
    const remainingAmount = goal.targetAmount - actualProgress;
    const remainingDays = getGoalRemainingDays(goal);

    return remainingDays > 0 ? remainingAmount / remainingDays : 0;
};

// Calculate adjusted end date (including paused time)
export const getGoalAdjustedEndDate = (goal) => {
    return addDays(goal.createdDate, goal.duration + (goal.totalPausedDays || 0));
};

// Get all calculated properties for a goal (for display purposes)
export const getGoalCalculatedProperties = (goal, transactions) => {
    try {
        return {
            status: getGoalStatus(goal, transactions),
            progress: getGoalProgress(goal.id, transactions),
            progressPercentage: getGoalProgressPercentage(goal, transactions),
            activeDaysElapsed: getGoalActiveDaysElapsed(goal),
            remainingDays: getGoalRemainingDays(goal),
            dailyAllocation: getGoalDailyAllocation(goal, transactions),
            adjustedEndDate: getGoalAdjustedEndDate(goal)
        };
    } catch (error) {
        console.error('Error calculating goal properties:', error, goal);

        // Return safe default values if calculation fails
        return {
            status: goal.status || 'active',
            progress: 0,
            progressPercentage: 0,
            activeDaysElapsed: 0,
            remainingDays: goal.duration || 0,
            dailyAllocation: 0,
            adjustedEndDate: goal.createdDate || new Date().toISOString()
        };
    }
};